import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { TMDB_MAX_DETAILS_LIMIT, type TmdbAdminDto, type TmdbSyncRunDto } from '@filmsloth/shared';
import { ApiError, api } from '../api/client.js';
import { useI18n, useT } from '../i18n/index.js';

/** How often the panel re-asks while a run is going. */
const POLL_MS = 5000;

/**
 * The TMDB sync on the operator's dashboard: is the catalog fresh, did the
 * last runs work, and a button to start one now with its own limit.
 *
 * Polls only while a run is going, so a dashboard left open overnight costs
 * nothing.
 */
export function TmdbSyncPanel() {
  const t = useT();
  const { locale } = useI18n();
  const [data, setData] = useState<TmdbAdminDto | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [detailsLimit, setDetailsLimit] = useState('500');
  const [discover, setDiscover] = useState(false);
  const [discoverPages, setDiscoverPages] = useState('');
  const [starting, setStarting] = useState(false);
  const [startError, setStartError] = useState<string | null>(null);

  const load = useCallback(() => {
    api
      .tmdbAdmin()
      .then((next) => {
        setData(next);
        setLoadFailed(false);
      })
      .catch(() => setLoadFailed(true));
  }, []);

  useEffect(load, [load]);

  const running = data?.running ?? false;
  useEffect(() => {
    if (!running) return;
    const timer = window.setInterval(load, POLL_MS);
    return () => window.clearInterval(timer);
  }, [running, load]);

  // The configured page count is the sensible default for a manual discover.
  useEffect(() => {
    if (data && discoverPages === '') setDiscoverPages(String(data.settings.discoverPages));
  }, [data, discoverPages]);

  const number = (value: number): string => value.toLocaleString(locale);
  const time = (iso: string | null): string => (iso ? new Date(iso).toLocaleString(locale) : '—');
  const duration = (run: TmdbSyncRunDto): string => {
    if (!run.finishedAt) return '…';
    const seconds = Math.round(
      (new Date(run.finishedAt).getTime() - new Date(run.startedAt).getTime()) / 1000,
    );
    return seconds < 120 ? `${seconds} s` : `${Math.round(seconds / 60)} min`;
  };

  const start = async (event: FormEvent) => {
    event.preventDefault();
    const limit = Number(detailsLimit);
    const pages = Number(discoverPages);
    if (!Number.isInteger(limit) || limit < 0 || limit > TMDB_MAX_DETAILS_LIMIT) {
      setStartError(t('tmdb.invalidLimit', { max: number(TMDB_MAX_DETAILS_LIMIT) }));
      return;
    }
    setStarting(true);
    setStartError(null);
    try {
      await api.startTmdbSync({
        detailsLimit: limit,
        discover,
        ...(discover && Number.isInteger(pages) && pages >= 0 ? { discoverPages: pages } : {}),
      });
      load();
    } catch (cause) {
      const code = cause instanceof ApiError ? cause.code : '';
      setStartError(
        code === 'sync_running'
          ? t('tmdb.alreadyRunning')
          : code === 'tmdb_disabled'
            ? t('tmdb.disabled')
            : t('tmdb.startFailed'),
      );
    } finally {
      setStarting(false);
    }
  };

  return (
    <div className="panel stack stack--tight">
      <div className="row row--between">
        <h2>{t('tmdb.title')}</h2>
        <button type="button" className="btn btn--ghost btn--small" onClick={load}>
          {t('tmdb.reload')}
        </button>
      </div>

      {loadFailed && !data ? <p className="muted small">{t('tmdb.loadFailed')}</p> : null}
      {data && !data.enabled ? <p className="banner">{t('tmdb.disabled')}</p> : null}

      {data?.enabled ? (
        <>
          {running ? (
            <p className="banner" role="status">
              {t('tmdb.running')}
            </p>
          ) : null}
          {data.usingFallback ? <p className="banner banner--warn">{t('tmdb.fallback')}</p> : null}

          {data.catalog ? (
            <div className="stats__tiles">
              <div className="stats__tile">
                <span className="stats__tile-value">{number(data.catalog.films)}</span>
                <span className="muted small">{t('tmdb.films')}</span>
              </div>
              <div className="stats__tile">
                <span className="stats__tile-value">{number(data.catalog.due)}</span>
                <span className="muted small">{t('tmdb.due')}</span>
                <span className="muted small">
                  {t('tmdb.withoutDetails', { count: number(data.catalog.withoutDetails) })}
                </span>
              </div>
              <div className="stats__tile">
                <span className="stats__tile-value tmdb__date">
                  {time(data.catalog.oldestDetailsAt)}
                </span>
                <span className="muted small">{t('tmdb.oldest')}</span>
              </div>
              <div className="stats__tile">
                <span className="stats__tile-value tmdb__date">{time(data.nextScheduledAt)}</span>
                <span className="muted small">{t('tmdb.next')}</span>
              </div>
            </div>
          ) : null}

          <p className="muted small">
            {t('tmdb.settings', {
              perRun: number(data.settings.detailsPerRun),
              pages: data.settings.discoverPages === 0 ? '∞' : number(data.settings.discoverPages),
              days: data.settings.detailsMaxAgeDays,
              hours: data.settings.intervalHours,
            })}
          </p>

          <h3>{t('tmdb.runsTitle')}</h3>
          <div className="stats__scroll">
            <table className="stats__table">
              <thead>
                <tr>
                  <th scope="col">{t('tmdb.colStarted')}</th>
                  <th scope="col">{t('tmdb.colStatus')}</th>
                  <th scope="col">{t('tmdb.colTrigger')}</th>
                  <th scope="col">{t('tmdb.colDuration')}</th>
                  <th scope="col">{t('tmdb.colDiscovered')}</th>
                  <th scope="col">{t('tmdb.colRefreshed')}</th>
                  <th scope="col">{t('tmdb.colGone')}</th>
                </tr>
              </thead>
              <tbody>
                {data.runs.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="muted">
                      {t('tmdb.noRuns')}
                    </td>
                  </tr>
                ) : null}
                {data.runs.map((run) => (
                  <tr key={run.id} title={run.error ?? undefined}>
                    <th scope="row">{time(run.startedAt)}</th>
                    <td className={`tmdb__status tmdb__status--${run.status}`}>
                      {t(`tmdb.status.${run.status}`)}
                      {run.error ? <span className="tmdb__error small">{run.error}</span> : null}
                    </td>
                    <td>{t(`tmdb.trigger.${run.trigger}`)}</td>
                    <td>{duration(run)}</td>
                    <td>{run.discoverPages === 0 ? '—' : number(run.discovered)}</td>
                    <td>
                      {t('tmdb.refreshedOf', {
                        done: number(run.detailed),
                        due: number(run.due),
                      })}
                    </td>
                    <td>{run.gone ? number(run.gone) : '·'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <form className="stack stack--tight tmdb__form" onSubmit={(event) => void start(event)}>
            <h3>{t('tmdb.manualTitle')}</h3>
            <p className="muted small">{t('tmdb.manualHint')}</p>
            <label className="field">
              <span className="field__label">{t('tmdb.detailsLimit')}</span>
              <input
                className="input"
                type="number"
                min={0}
                max={TMDB_MAX_DETAILS_LIMIT}
                inputMode="numeric"
                value={detailsLimit}
                onChange={(event) => setDetailsLimit(event.target.value)}
              />
            </label>
            <label className="row tmdb__checkbox">
              <input
                type="checkbox"
                checked={discover}
                onChange={(event) => setDiscover(event.target.checked)}
              />
              <span>{t('tmdb.discover')}</span>
            </label>
            {discover ? (
              <label className="field">
                <span className="field__label">{t('tmdb.discoverPages')}</span>
                <input
                  className="input"
                  type="number"
                  min={0}
                  max={500}
                  inputMode="numeric"
                  value={discoverPages}
                  onChange={(event) => setDiscoverPages(event.target.value)}
                />
              </label>
            ) : null}
            {startError ? <p className="banner banner--error">{startError}</p> : null}
            <button
              type="submit"
              className="btn btn--primary btn--block"
              disabled={starting || running}
            >
              {running ? t('tmdb.running') : t('tmdb.start')}
            </button>
          </form>
        </>
      ) : null}
    </div>
  );
}
